import React, { useState } from 'react';
import { formatKey, formatValue } from '../../../common/Utils/Utils';

/**
 * AccordionItem Component - Renders a single accordion section
 * Supports nested accordions and different data types
 *
 * @param {string} title - The accordion header title
 * @param {*} data - The data to display (object, array, or primitive)
 * @param {boolean} isOpen - Whether the accordion is open
 * @param {function} onToggle - Function to toggle accordion state
 * @param {number} level - Nesting level (0 for root)
 * @param {string} parentKey - Unique key for nested items
 */
const AccordionItem = ({
  title,
  data,
  isOpen,
  onToggle,
  level = 0,
  parentKey = '',
}) => {
  const [nestedOpen, setNestedOpen] = useState({});

  // Toggle nested accordion independently
  const toggleNested = (key) => {
    setNestedOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderContent = () => {
    // If data is an object with nested structure
    if (typeof data === 'object' && !Array.isArray(data)) {
      return (
        <div className="data-list">
          {Object.entries(data).map(([key, value]) => {
            // If value is an object or non-empty array, render as nested accordion
            if (
              typeof value === 'object' &&
              value !== null &&
              ((Array.isArray(value) && value.length > 0) ||
                !Array.isArray(value))
            ) {
              const nestedKey = `${parentKey}_${key}`;
              return (
                <AccordionItem
                  key={key}
                  title={key}
                  data={value}
                  isOpen={nestedOpen[key]}
                  onToggle={() => toggleNested(key)}
                  level={level + 1}
                  parentKey={nestedKey}
                />
              );
            }
            // Simple key-value pair
            return (
              <div key={key} className="br_7 data-row flex-column p-2 mb-2">
                <span className="data-label font_500 font_13">
                  {formatKey(key)}
                </span>
                <span className="data-value font_500 font_15">
                  {formatValue(value)}
                </span>
              </div>
            );
          })}
        </div>
      );
    }

    // If data is an array
    if (Array.isArray(data)) {
      // Empty array
      if (data.length === 0) {
        return (
          <div className="p-2">
            <span className="text-muted font_13">No items</span>
          </div>
        );
      }

      // Array with items - show as bullet points
      return (
        <ul className="list-unstyled ps-3 mb-0">
          {data.map((item, index) => (
            <li key={index} className="mb-2">
              <div className="d-flex align-items-start">
                <span className="me-2">•</span>
                <span className="data-value font_500 font_14">{item}</span>
              </div>
            </li>
          ))}
        </ul>
      );
    }

    // Primitive value
    return (
      <div className="br_7 data-row flex-column p-2">
        <span className="data-value font_500 font_15">{formatValue(data)}</span>
      </div>
    );
  };

  return (
    <div
      className={`accordion-item border rounded mb-2 ${
        level > 0 ? 'ms-3' : ''
      }`}
    >
      <div
        className="accordion-header p-3 d-flex justify-content-between align-items-center"
        onClick={onToggle}
        style={{
          cursor: 'pointer',
          backgroundColor: level === 0 ? '#f8f9fa' : '#ffffff',
        }}
      >
        <h6 className={`mb-0 font_${level === 0 ? '14' : '13'} fw-semibold`}>
          {formatKey(title)}
        </h6>
        <i
          className={`fas fa-chevron-${isOpen ? 'up' : 'down'} font_12`}
          style={{ transition: 'transform 0.3s ease' }}
        ></i>
      </div>
      {isOpen && <div className="accordion-body p-3">{renderContent()}</div>}
    </div>
  );
};

export default AccordionItem;
